import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { MAIN_SITE_URL } from "@/data/categories";
import SiteNav from "@/components/feature/SiteNav";
import SiteFooter from "@/components/feature/SiteFooter";
import Hero from "./components/Hero";
import CategorySection from "./components/CategorySection";
import { ResourceGrid, PopularGrid, HighlightSection } from "./components/ContentSections";
import GuidesSection from "./components/GuidesSection";
import FaqSection from "./components/FaqSection";

export default function Home() {
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    document.title = t("hero.title");
    document.documentElement.lang = i18n.language;
  }, [i18n.language, t]);

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900">
      <SiteNav />
      <main>
        <Hero />
        <CategorySection />
        <ResourceGrid />

        <HighlightSection
          id="spreadsheet"
          icon="ri-table-line"
          title={t("spreadsheet.title")}
          text={t("spreadsheet.text")}
          ctaLabel={t("spreadsheet.cta")}
          ctaTo="/usfans-spreadsheet-guide"
        />

        <PopularGrid />

        <HighlightSection alt icon="ri-compass-3-line" title={t("confidence.title")} text={t("confidence.text")} />

        <HighlightSection
          id="shipping"
          icon="ri-truck-line"
          title={t("shipping.title")}
          text={t("shipping.text")}
          ctaLabel={t("shipping.cta")}
          ctaTo="/usfans-spreadsheet-guide#shipping"
        />

        <HighlightSection
          id="review"
          alt
          icon="ri-star-line"
          title={t("review.title")}
          text={t("review.text")}
          ctaLabel={t("review.cta")}
          ctaTo="/usfans-spreadsheet-guide"
        />

        <HighlightSection
          id="safety"
          icon="ri-shield-check-line"
          title={t("safety.title")}
          text={t("safety.text")}
          ctaLabel={t("safety.cta")}
          ctaTo="/usfans-spreadsheet-guide#safety"
        />

        <GuidesSection />
        <FaqSection />

        <section className="bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="text-2xl font-bold text-foreground-950 md:text-4xl">{t("finalCta.title")}</h2>
              <p className="mt-3 text-base leading-relaxed text-foreground-600">{t("finalCta.text")}</p>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                {t("finalCta.cta")}
                <i className="ri-arrow-right-up-line text-base" />
              </a>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}